(() => {
  if (customElements.get('a2l-story-nav')) return;
  class A2lStoryNav extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => this.initialize());
    }
    initialize() {
      this.frame = 0;
      this.events = new AbortController();
      const signal = this.events.signal;
      this.root = this.closest('.shopify-section') || this;
      this.links = [...this.querySelectorAll('a[href^="#"]')];
      this.header = document.querySelector('.shopify-section header.header') || document.querySelector('header.header');
      this.headerState = this.header ? { translate: this.header.style.translate, inert: this.header.inert } : null;
      this.shift = 0;
      this.originalMargin = this.root.style.marginBlockStart;
      this.root.style.marginBlockStart = getComputedStyle(this.root).marginBlockStart;
      this.marker = document.createElement('span');
      this.marker.setAttribute('aria-hidden', 'true');
      this.marker.style.cssText = 'position:absolute;display:block;width:0;height:0;pointer-events:none';
      this.root.before(this.marker);
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.margins = new Map();
      this.focusedTargets = new Set();
      this.refreshTargets();
      this.links.forEach((link) => link.addEventListener('click', (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const target = document.getElementById(link.hash.slice(1));
        if (!target) return;
        event.preventDefault();
        this.update();
        history.pushState(null, '', link.hash);
        if (!target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
          this.focusedTargets.add(target);
          target.addEventListener('blur', () => {
            target.removeAttribute('tabindex');
            this.focusedTargets.delete(target);
          }, { once: true, signal });
        }
        target.focus({ preventScroll: true });
        const top = target.getBoundingClientRect().top + window.scrollY - this.offset;
        window.scrollTo({ top: Math.max(0, top), behavior: this.reduced.matches ? 'instant' : 'smooth' });
        this.markCurrent(link);
      }, { signal }));
      window.addEventListener('scroll', () => this.schedule(), { passive: true, signal });
      window.addEventListener('resize', () => this.schedule(), { passive: true, signal });
      document.addEventListener('shopify:section:load', () => {
        const currentHeader = document.querySelector('header.header');
        if (currentHeader !== this.header) {
          this.restoreHeader();
          this.header = currentHeader;
          this.headerState = currentHeader ? { translate: currentHeader.style.translate, inert: currentHeader.inert } : null;
        }
        this.refreshTargets();
        this.schedule();
      }, { signal });
      this.accountMutation = new MutationObserver(() => this.schedule());
      this.accountMutation.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
      if (this.header) {
        const headerSection = this.header.closest('.shopify-section') || this.header;
        headerSection.addEventListener('transitionend', () => this.schedule(), { signal });
        this.headerMutation = new MutationObserver(() => this.schedule());
        this.headerMutation.observe(headerSection, { attributes: true, attributeFilter: ['class', 'style'], subtree: false });
      }
      if ('ResizeObserver' in window) {
        this.resize = new ResizeObserver(() => this.schedule());
        this.resize.observe(this);
        if (this.header) this.resize.observe(this.header);
      }
      this.update();
    }
    refreshTargets() {
      this.targets = this.links.map((link) => ({ link, target: document.getElementById(link.hash.slice(1)) })).filter(({ target }) => target);
      this.observer?.disconnect();
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(() => this.schedule(), { threshold: [0, 0.1, 1] });
        this.targets.forEach(({ target }) => this.observer.observe(target));
      }
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = 0;
        this.update();
      });
    }
    update() {
      if (!this.isConnected) return;
      const rect = this.header?.getBoundingClientRect();
      const naturalTop = this.marker.getBoundingClientRect().top + parseFloat(getComputedStyle(this.root).marginBlockStart || 0);
      if (rect && !document.documentElement.classList.contains('header-account-open')) {
        const baseBottom = rect.bottom - this.shift;
        this.shift = Math.min(0, Math.max(-Math.max(0, baseBottom), naturalTop - 8 - baseBottom));
        this.header.style.translate = `0 ${this.shift}px`;
        this.header.inert = this.headerState.inert || baseBottom + this.shift <= 0;
      }
      this.offset = this.getBoundingClientRect().height + 16;
      let current = this.targets[0]?.link;
      this.targets.forEach(({ target, link }) => {
        if (!this.margins.has(target)) this.margins.set(target, target.style.scrollMarginTop);
        target.style.scrollMarginTop = this.offset + 'px';
        if (target.getBoundingClientRect().top <= this.offset + 2) current = link;
      });
      if (current) this.markCurrent(current);
    }
    markCurrent(current) {
      this.links.forEach((link) => {
        if (link === current) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }
    restoreHeader() {
      if (this.header && this.headerState) {
        this.header.style.translate = this.headerState.translate;
        this.header.inert = this.headerState.inert;
      }
      this.shift = 0;
    }
    disconnectedCallback() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.events?.abort();
      this.observer?.disconnect();
      this.resize?.disconnect();
      this.headerMutation?.disconnect();
      this.accountMutation?.disconnect();
      this.restoreHeader();
      this.marker?.remove();
      if (this.root) this.root.style.marginBlockStart = this.originalMargin || '';
      this.margins?.forEach((value, target) => { target.style.scrollMarginTop = value; });
      this.focusedTargets?.forEach((target) => target.removeAttribute('tabindex'));
    }
  }
  customElements.define('a2l-story-nav', A2lStoryNav);
})();

